<template>
	<section id="user-profile" class="user-profile" :key="userProfile.userId">
		<!-- Add Social Media Accounts Modal -->
		<modal
			modalTitle="Add Social Media"
			actionBtnName="Close"
			class="lg social-post-modal disable-padding disable-cancel"
			v-if="showModal"
			@close="showModal = false"
			@clicked="showModal = false"
		>
			<div slot="modalContent">
				<SocialAccounts></SocialAccounts>
			</div>
		</modal>
		<modal
			modalTitle="Review and Save Disclaimers"
			actionBtnName="Close"
			class="lg legal-modal disable-cancel"
			v-if="showDisclaimersNotificationModal"
			@close="showDisclaimersNotificationModal = false"
			@clicked="showDisclaimersNotificationModal = false"
		>
			<div slot="modalContent">
				<p>
					A change you made to your settings may have affected your disclaimers.
					Please review your
					<router-link to="/preference-center/disclaimers"
						>Disclaimers</router-link
					>
					and update if necessary.
				</p>
			</div>
		</modal>

		<modal
			v-if="showSaveModal"
			:modalTitle="saveModalTitle"
			actionBtnName="Okay"
			class="lg save-modal"
			@close="SHOW_SAVE_MODAL()"
			@clicked="SHOW_SAVE_MODAL()"
		>
			<div slot="modalContent">
				<p>{{ saveModalMsg }}</p>
			</div>
		</modal>

		<!--Profile Photo-->
		<section
			class="b360-grid-row preference-center-section preference-center-section--brand"
		>
			<TitleDescription
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 title-description"
				:title="SECTION.PROFILE_PHOTO.TITLE"
				:description="SECTION.PROFILE_PHOTO.DESCRIPTION"
			/>
			<CloudinaryUploader
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				:label="SECTION.PROFILE_PHOTO.TITLE"
				:id="'user-profile'"
				:uri="profilePhotoImageUri"
				:disabled="roleIsNotActive('UploadAHeadshot')"
				@delete="handleDeleteProfilePhoto"
				@upload="handleUploadProfilePhoto"
			/>
		</section>

		<!--Company Brand-->
		<section
			class="b360-grid-row preference-center-section preference-center-section--brand"
		>
			<TitleDescription
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 title-description"
				:title="SECTION.COMPANY_BRAND.TITLE"
				:description="SECTION.COMPANY_BRAND.DESCRIPTION"
			/>
			<CloudinaryUploader
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 cloudinary-uploader"
				label="Company Logo"
				id="company-profile"
				:uri="personalCompanyLogoImageUri"
				:disabled="roleIsNotActive('UploadALogo')"
				@delete="handleDeleteCompanyLogo"
				@upload="handleUploadCompanyLogo"
			/>
			<div
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 brand-items--wrapper"
			>
				<ColorPicker
					class="colorpicker--userprofile"
					title="Brand Colors"
					name="Color1"
					:color="brand.Color1.value"
					:disabled="roleIsNotActive('SelectBrandColors')"
					@input="handleBrandInput"
					data-test="Color1"
				/>
				<ColorPicker
					class="colorpicker--userprofile"
					name="Color2"
					:color="brand.Color2.value"
					:disabled="roleIsNotActive('SelectBrandColors')"
					@input="handleBrandInput"
					data-test="Color2"
				/>
				<ColorPicker
					class="colorpicker--userprofile last"
					name="Color3"
					:color="brand.Color3.value"
					:disabled="roleIsNotActive('SelectBrandColors')"
					@input="handleBrandInput"
					data-test="Color3"
				/>
				<div>
					<FontDropdown
						name="HeaderFontFamily"
						ref="HeaderFontFamily"
						label="Font Family"
						v-model="$v.brand.HeaderFontFamily.value.$model"
						:value="$v.brand.HeaderFontFamily.value.$model"
						:required="false"
						:options="FONT_OPTIONS_ARRAY"
						:disabled="roleIsNotActive('SelectAFontFamily')"
						@input="handleBrandInput"
					/>
				</div>
			</div>
		</section>

		<!-- Personal Details -->
		<section class="b360-grid-row preference-center-section">
			<TitleDescription
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 title-description--span8"
				:title="SECTION.PERSONAL_DETAILS.TITLE"
				:description="SECTION.PERSONAL_DETAILS.DESCRIPTION"
			/>
			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					name="firstName"
					ref="firstName"
					label="First Name"
					type="text"
					placeholder="Jon"
					maxLength="100"
					v-model="$v.profile.firstName.$model"
					:required="true"
					:disabled="roleIsNotActive('EditAPersonalName')"
					:error="!$v.profile.firstName.required"
				/>
				<span class="error" v-if="!$v.profile.firstName.required"
					>First name is required.</span
				>
			</div>

			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					name="lastName"
					ref="lastName"
					label="Last Name"
					type="text"
					placeholder="Doe"
					maxLength="100"
					v-model="$v.profile.lastName.$model"
					:required="true"
					:disabled="roleIsNotActive('EditAPersonalName')"
					:error="!$v.profile.lastName.required"
				/>
				<span class="error" v-if="!$v.profile.lastName.required"
					>Last name is required.</span
				>
			</div>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="preferredName"
				label="Preferred First Name"
				type="text"
				placeholder="Jon"
				v-model="profile.preferredName"
				:maxLength="100"
				:required="false"
				:disabled="roleIsNotActive('EditAPersonalName')"
			/>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="doingBusinessAs"
				label="Doing Business As"
				type="text"
				placeholder="Your DBA Name"
				v-model="profile.doingBusinessAs"
				:maxLength="100"
				:required="false"
				:disabled="roleIsNotActive('EditAPersonalName')"
			/>

			<div
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				data-test="email"
			>
				<FormInputs
					name="email"
					ref="email"
					label="Email Address"
					type="text"
					placeholder="jondoe@domain.com"
					v-model="$v.profile.email.$model"
					:maxLength="100"
					:required="true"
					:disabled="roleIsNotActive('EditAPersonalName')"
					:error="isInvalidEmail || isNoEmail"
				/>
				<span class="error" data-test="no-email" v-if="isNoEmail"
					>Email is required.</span
				>
				<span class="error" data-test="invalid-email" v-if="isInvalidEmail"
					>Invalid Email</span
				>
			</div>

			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					name="websiteUrl"
					ref="websiteUrl"
					label="Website"
					type="text"
					placeholder="www.websitegoeshere.com"
					v-model="$v.profile.websiteUrl.$model"
					:maxLength="61"
					:required="true"
					:disabled="roleIsNotActive('EditAPersonalName')"
					:error="!$v.profile.websiteUrl.required"
				/>
				<span class="error" v-if="!$v.profile.websiteUrl.required"
					>Website is required.</span
				>
			</div>

			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					name="phoneNumber"
					ref="phoneNumber"
					label="Phone Number"
					type="phone"
					placeholder="555-555-5555"
					v-model="$v.profile.phoneNumber.$model"
					:mask="PHONE_MASKS.PHONE_NUMBER_MASK"
					:maxLength="61"
					:required="true"
					:disabled="
						roleIsNotActive('EditAPersonalPhoneNumberAbilityToAddExtension')
					"
					:error="!$v.profile.phoneNumber.required"
				/>
				<span
					class="error"
					v-if="!$v.profile.phoneNumber.required"
					data-test="phone-required"
					>Phone Number is required.
				</span>
				<span
					class="error"
					v-if="
						isCorrectLength(profile.phoneNumber, 12) &&
						$v.profile.phoneNumber.required
					"
					data-test="correct-phone-length"
				>
					Phone Number must have 10 digits.
				</span>
			</div>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="phoneExtension"
				label="Phone Extension"
				type="tel"
				placeholder="1234"
				v-model="profile.phoneExtension"
				:maxLength="61"
				:mask="PHONE_MASKS.PHONE_NUMBER_EXTENSION_MASK"
				:required="false"
				:disabled="
					roleIsNotActive('EditAPersonalPhoneNumberAbilityToAddExtension')
				"
			/>

			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					class="b360-col-xs-12 b360-col-md-4"
					name="mobilePhoneNumber"
					ref="mobilePhoneNumber"
					label="Cell Phone Number"
					type="phone"
					placeholder="555-555-5555"
					:maxLength="61"
					v-model="$v.profile.mobilePhoneNumber.$model"
					:mask="PHONE_MASKS.PHONE_NUMBER_MASK"
					:required="true"
					:disabled="
						roleIsNotActive(
							'EditAPersonalSecondPhoneNumberForCellOrBusinessLine',
						)
					"
					:error="!$v.profile.mobilePhoneNumber.required"
				/>
				<span class="error" v-if="!$v.profile.mobilePhoneNumber.required"
					>Mobile Number is required.</span
				>
				<span
					v-if="
						isCorrectLength(profile.mobilePhoneNumber, 12) &&
						$v.profile.mobilePhoneNumber.required
					"
					class="error"
					data-test="correct-mobile-length"
				>
					Mobile Number must have 10 digits.
				</span>
			</div>

			<FormInputs
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="nmlsNumber"
				label="NMLS"
				type="text"
				placeholder="1234567890"
				v-model="profile.nmlsNumber"
				:required="false"
				:disabled="true"
			/>

			<FormInputs
				v-if="hasCaliforniaLicenses"
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="BrokerDreLicense"
				label="DRE: Real Estate Broker License"
				type="text"
				placeholder="1234567890"
				v-model="profile.contactLicenses.BrokerDreLicense"
				:required="false"
				:disabled="roleIsNotActive('EditAPersonalName')"
			/>

			<FormInputs
				v-if="hasCaliforniaLicenses"
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				name="SalespersonDreLicense"
				label="DRE: Real Estate Salesperson License"
				type="text"
				placeholder="1234567890"
				v-model="profile.contactLicenses.SalespersonDreLicense"
				:required="false"
				:disabled="roleIsNotActive('EditAPersonalName')"
			/>

			<div
				v-if="showBigMacField"
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
			>
				<div class="generate-link--wrapper">
					<div class="generate-link--field">
						<FormLabelTooltip
							:required="false"
							name="bigMacLink"
							label="Vendor Link"
						/>
						<div class="b360-form-input">
							<a
								target="_blank"
								rel="noreferrer"
								:href="userProfile.bigMacLink"
								>{{ userProfile.bigMacLink }}</a
							>
						</div>
					</div>

					<button
						class="b360-btn b360-btn--primary generate-link--btn"
						:class="{
							'b360-btn--disabled': isImpersonated || !!userProfile.bigMacLink,
						}"
						:disabled="isImpersonated || !!userProfile.bigMacLink"
						@click.prevent="generateVendorLink()"
					>
						Generate
					</button>
				</div>
			</div>
		</section>

		<!--BLINK+-->
		<section class="b360-grid-row preference-center-section">
			<TitleDescription
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 title-description"
				:title="SECTION.BLINK.TITLE"
				:description="SECTION.BLINK.DESCRIPTION"
			/>
			<div class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4">
				<FormInputs
					name="blinkLink"
					ref="blinkLink"
					type="text"
					:label="BLINK_DETAILS.LABEL"
					placeholder="https://www.blink.mortgage/"
					:maxLength="200"
					:required="false"
					v-model="profile.blinkLink"
					:disabled="roleIsNotActive('EditAPersonalName') || !hasBlink"
					:error="!$v.profile.blinkLink.validateURLString"
				/>
				<span
					class="error"
					data-test="blink-error"
					v-if="!$v.profile.blinkLink.validateURLString"
					>Please enter valid Blink+ URL.</span
				>
			</div>
			<CloudinaryUploader
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 cloudinary-uploader"
				id="blinkQRCodeImage"
				:uri="blinkQRCodeImageUri"
				label="Blink+ QR Code"
				:disabled="roleIsNotActive('EditAPersonalName') || !hasBlink"
				@delete="handleDeleteBlinkQRCode"
				@upload="handleUploadBlinkQRCode"
			/>
		</section>

		<!-- Mailing Address -->
		<MailingAddressesLayout
			:profileAddresses="profile.addresses"
			@update:profileAddresses="profile.addresses = $event"
			:profileSaved="savedState"
		/>
		<!-- About (Famb) Section -->
		<section
			class="b360-grid-row preference-center-section preference-center-section--about"
			v-if="hasFambFeature && hasAccessToFamb"
		>
			<TitleDescriptionLink
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 form-title-description title-description--span2"
				linkDescription="click here."
				:title="SECTION.ABOUT.TITLE"
				:description="SECTION.ABOUT.DESCRIPTION"
				@handle-click="aboutDescriptionClickHandler"
			/>
			<FormTextarea
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-8 b360-col-lg-8 about-input"
				id="aboutLO"
				name="aboutLO"
				label="About"
				:labelHidden="true"
				v-model="profile.aboutLO"
				:maxLength="1024"
				:showMaxLengthIndicator="true"
				:required="false"
				:disabled="mainProfileIsInternalUser"
			/>
		</section>
		<!-- Social Media Section -->
		<section class="b360-grid-row preference-center-section">
			<TitleDescriptionLink
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4 form-title-description title-description--span3"
				:title="SECTION.SOCIAL_MEDIA.TITLE"
				:description="SECTION.SOCIAL_MEDIA.DESCRIPTION"
				linkDescription="click here."
				@handle-click="showModal = true"
			/>

			<div
				class="b360-col-xs-12 b360-col-sm-12 b360-col-md-4"
				v-for="socialItem in SOCIAL_MEDIA_DETAILS"
				:key="socialItem.name"
				:data-test="socialItem.name"
			>
				<FormInputs
					type="text"
					:id="socialItem.name"
					:name="socialItem.name"
					:ref="socialItem.name"
					:label="socialItem.label"
					:placeholder="socialItem.placeholder"
					:required="false"
					:maxLength="250"
					:disabled="roleIsNotActive(socialItem.permissionName)"
					:error="!$v.socialMediaDetails[socialItem.name].validateURLString"
					v-model="socialMediaDetails[socialItem.name]"
					@handle-input="handleSocialMediaDetailsInput"
				/>
				<span
					class="error"
					v-if="!$v.socialMediaDetails[socialItem.name].validateURLString"
					>Please enter a valid {{ socialItem.label }} account URL.</span
				>
			</div>
		</section>

		<SaveButton
			:waiting="savingState"
			:saved="savedState"
			@handle-click="saveProfile"
			:disabled="disableSaveButton"
		/>
	</section>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { cloneDeep, isEqual } from 'lodash';
import ColorPicker from '@/components/brand-builder/ColorPicker.vue';
import CloudinaryUploader from '@/components/preference-center/CloudinaryUploader.vue';
import TitleDescription from '@/components/global/TitleDescription.vue';
import TitleDescriptionLink from '@/components/global/TitleDescriptionLink.vue';
import FormInputs from '@/components/global/FormInputs.vue';
import FormTextarea from '@/components/global/FormTextarea.vue';
import FontDropdown from '@/components/preference-center/FontDropdowns.vue';
import StateDropdown from '@/components/preference-center/StateDropdowns.vue';
import SaveButton from '@/components/preference-center/SaveButton.vue';
import Modal from '@/components/global/Modal.vue';
import SocialAccounts from '@/components/vendasta/SocialAccounts.vue';
import FormLabelTooltip from '@/components/global/FormLabelTooltip.vue';
import {
	startsWithCorrectURL,
	updateSocialMediaDetailsArray,
	updateBrandArray,
	makeSocialMediaDetails,
	makeBrand,
	findFirstInvalidItem,
} from './helpers';
import {
	BLINK_DETAILS,
	BRAND_ENTITY,
	FONT_OPTIONS_ARRAY,
	PHONE_MASKS,
	SECTION,
	SOCIAL_MEDIA_URL,
	SOCIAL_MEDIA_DETAILS,
	SOCIAL_MEDIA_DETAILS_ENTITY,
} from './constants';
import { required, email } from 'vuelidate/lib/validators';
import colorExtractor from '@/util/color-extractor';
import leadsService from '@/services/contacts/leads-service.js';
import MailingAddressesLayout from '@/components/preference-center/MailingAddressesLayout.vue';

export default {
	name: 'PreferenceCenterUser',
	components: {
		CloudinaryUploader,
		TitleDescription,
		TitleDescriptionLink,
		FormInputs,
		FormLabelTooltip,
		FormTextarea,
		ColorPicker,
		FontDropdown,
		StateDropdown,
		SaveButton,
		Modal,
		SocialAccounts,
		MailingAddressesLayout,
	},
	data() {
		return {
			brand: { ...BRAND_ENTITY },
			profile: {},
			profilePhotoImageUri: '',
			personalCompanyLogoImageUri: '',
			blinkQRCodeImageUri: '',
			socialMediaDetails: { ...SOCIAL_MEDIA_DETAILS_ENTITY },
			showModal: false,
			showDisclaimersNotificationModal: false,
			savingState: false,
			savedState: false,
			originalSocialVInfo: null,
			socialMediaURLs: SOCIAL_MEDIA_URL,
		};
	},
	computed: {
		...mapGetters([
			'currentUserRolodexIds',
			'hasAccessToFamb',
			'hasBlink',
			'isImpersonated',
			'roleIsNotActive',
		]),
		...mapState({
			userProfile: (state) => state.profile,
			disableSave: (state) =>
				state.profileSwitcher.profileSelector.isInternalUser,
			mainProfileIsInternalUser: (state) =>
				state.profileSwitcher.mainProfileSelector.isInternalUser,
			hasSocialV: (state) => state.profile.hasSocialVAccount,
			hasCaliforniaLicenses: (state) =>
				state.licenses.list.some((el) => el.state.startsWith('CA')),
			saveModalMsg: (state) => state.profile.saveModalMsg,
			saveModalTitle: (state) => state.profile.saveModalTitle,
			showSaveModal: (state) => state.profile.showSaveModal,
			showBigMacField: (state) => state.companyProfile.bigMacFeatureFlagInd,
			hasFambFeature: (state) => state.companyProfile.fambFeatureFlagIndicator,
			userSocialMediaDetails: (state) => state.profile.socialMediaDetails,
			addresses: (state) => state.profile.addresses,
			userProfileImage: (state) => state.profile.profilePhoto,
			userId: (state) => state.profile.userId,
			contactId: (state) => state.profile.contactId,
		}),
		isInvalidEmail() {
			return !this.$v.profile.email.email;
		},
		isNoEmail() {
			return !this.$v.profile.email.required;
		},
		firstNameChanged() {
			return !(this.profile.firstName === this.originalSocialVInfo.firstName);
		},
		lastNameChanged() {
			return !(this.profile.lastName === this.originalSocialVInfo.lastName);
		},
		mobileNumberChanged() {
			return !(
				this.profile.mobilePhoneNumber === this.originalSocialVInfo.phoneNumber
			);
		},
		websiteUrlChanged() {
			return !(this.profile.websiteUrl === this.originalSocialVInfo.websiteUrl);
		},
		socialVLogoUriChanged() {
			return !(
				this.personalCompanyLogoImageUri === this.originalSocialVInfo.logoUrl
			);
		},
		socialVAccountChanged() {
			return (
				this.hasSocialV &&
				(this.firstNameChanged ||
					this.lastNameChanged ||
					this.websiteUrlChanged ||
					this.mobileNumberChanged ||
					this.socialVLogoUriChanged)
			);
		},
		isValidFacebook() {
			return this.$v.socialMediaDetails.FaceBook.validateURLString;
		},
		isValidInstagram() {
			return this.$v.socialMediaDetails.Instagram.validateURLString;
		},
		isValidLinkedIn() {
			return this.$v.socialMediaDetails.LinkedIn.validateURLString;
		},
		isValidTwitter() {
			return this.$v.socialMediaDetails.Twitter.validateURLString;
		},
		isValidTikTok() {
			return this.$v.socialMediaDetails.TikTok.validateURLString;
		},
		allSocialMediaIsValid() {
			return (
				this.isValidFacebook &&
				this.isValidInstagram &&
				this.isValidLinkedIn &&
				this.isValidTikTok &&
				this.isValidTwitter
			);
		},
		isValidMobileNumber() {
			return !(
				this.isCorrectLength(this.profile.mobilePhoneNumber, 12) &&
				this.$v.profile.mobilePhoneNumber.required
			);
		},
		isValidPhoneNumber() {
			return !(
				this.isCorrectLength(this.profile.phoneNumber, 12) &&
				this.$v.profile.phoneNumber.required
			);
		},
		isAnyAddressInvalid() {
			return this.profile.addresses.some((address) => {
				return Object.keys(address).some((property) => {
					return (
						property !== 'displayNumber' &&
						property !== 'addressLine2' &&
						address[property] === ''
					);
				});
			});
		},
		disableSaveButton() {
			return (
				this.disableSave ||
				this.isNoEmail ||
				this.isInvalidEmail ||
				!this.allSocialMediaIsValid ||
				!this.isValidMobileNumber ||
				!this.isValidPhoneNumber ||
				this.isAnyAddressInvalid
			);
		},
	},
	watch: {
		userProfile: {
			handler: 'updateProfileFromState',
			immediate: true,
			deep: true,
		},
	},
	validations: {
		brand: {
			HeaderFontFamily: {
				value: {},
			},
		},
		profile: {
			firstName: {
				required,
			},
			lastName: {
				required,
			},
			email: {
				required,
				email,
			},
			websiteUrl: {
				required,
			},
			phoneNumber: {
				required,
			},
			mobilePhoneNumber: {
				required,
			},
			blinkLink: {
				validateURLString: startsWithCorrectURL(BLINK_DETAILS.BLINK_URL),
			},
		},
		socialMediaDetails: {
			FaceBook: {
				validateURLString: startsWithCorrectURL(SOCIAL_MEDIA_URL.FACEBOOK),
			},
			LinkedIn: {
				validateURLString: startsWithCorrectURL(SOCIAL_MEDIA_URL.LINKEDIN),
			},
			Twitter: {
				validateURLString: startsWithCorrectURL(SOCIAL_MEDIA_URL.TWITTER),
			},
			Instagram: {
				validateURLString: startsWithCorrectURL(SOCIAL_MEDIA_URL.INSTAGRAM),
			},
			TikTok: {
				validateURLString: startsWithCorrectURL(SOCIAL_MEDIA_URL.TIKTOK),
			},
		},
	},
	methods: {
		...mapActions([
			'generateVendorLink',
			'resetSaveUserInCCV2Flag',
			'saveUserProfile',
			'updateSocialVAccount',
			'setBlinkIntegrationInd',
			'removeAdditionalAddress',
			'deleteAddress',
			'setAddressOrder',
		]),
		// TODO: USE ACTION INSTEAD OF MUTATION
		...mapMutations(['SHOW_SAVE_MODAL']),
		isCorrectLength(string, number) {
			if (string === null || string === '') {
				return false;
			} else if (number === 12) {
				return string.length < 12;
			} else if (number === 5) {
				return string.length < 5;
			} else return false;
		},
		handleUploadProfilePhoto({ format, secureUrl, publicId }) {
			this.profilePhotoImageUri = secureUrl;
			this.profile.profilePhoto = {
				imageFormat: format,
				imageUri: secureUrl,
				referenceId: publicId,
			};
		},
		handleDeleteProfilePhoto() {
			this.profilePhotoImageUri = '';
			this.profile.profilePhoto = {
				imageFormat: '',
				imageUri: '',
				referenceId: '',
			};
		},
		async handleUploadCompanyLogo({ format, secureUrl, publicId }) {
			this.personalCompanyLogoImageUri = secureUrl;
			this.profile.personalCompanyLogo = {
				imageFormat: format,
				imageUri: secureUrl,
				referenceId: publicId,
			};

			// Update the brand colors based on image
			const colors = await colorExtractor.updateColorsUserProfile(secureUrl);
			this.brand = { ...this.brand, ...colors };
			const colorValues = Object.values(colors);
			// sync with brand array on profile
			colorValues.forEach((color) => {
				updateBrandArray(color, this.profile.brand);
			});
		},
		handleDeleteCompanyLogo() {
			this.personalCompanyLogoImageUri = '';
			this.profile.personalCompanyLogo = {
				imageFormat: '',
				imageUri: '',
				referenceId: '',
			};
		},
		handleBrandInput(event) {
			const brandObject = {
				name: event.fieldName,
				value: event.value,
			};
			updateBrandArray(brandObject, this.profile.brand);
			this.brand[brandObject.name] = brandObject;
		},
		handleUploadBlinkQRCode({ format, secureUrl, publicId }) {
			this.blinkQRCodeImageUri = secureUrl;
			this.profile.blinkQRCode = {
				imageFormat: format,
				imageUri: secureUrl,
				referenceId: publicId,
			};
		},
		handleDeleteBlinkQRCode() {
			this.blinkQRCodeImageUri = '';
			this.profile.blinkQRCode = {
				imageFormat: '',
				imageUri: '',
				referenceId: '',
			};
		},
		handleSocialMediaDetailsInput(event) {
			updateSocialMediaDetailsArray(event, this.profile.socialMediaDetails);
		},
		updateProfileFromState(newProfile) {
			this.profile = cloneDeep(newProfile);

			/** Using separate data variables for the items below primarily because if
			they are null/undefined they should not be added to the user profile until
			the user adds them. Still, we need default values to use on the form. */
			this.socialMediaDetails = makeSocialMediaDetails(
				this.profile.socialMediaDetails,
			);
			this.brand = makeBrand(this.profile.brand);
			this.profilePhotoImageUri = this.profile.profilePhoto
				? this.profile.profilePhoto.imageUri
				: '';
			this.personalCompanyLogoImageUri = this.profile.personalCompanyLogo
				? this.profile.personalCompanyLogo.imageUri
				: '';
			this.blinkQRCodeImageUri = this.profile.blinkQRCode
				? this.profile.blinkQRCode.imageUri
				: '';
		},
		async saveProfile() {
			this.$v.$touch();
			if (!this.$v.$invalid) {
				this.savingState = true;
				try {
					await this.saveUserProfile(this.profile);
					if (this.userProfile.showDisclaimerNotification) {
						this.showDisclaimersNotificationModal = true;
					}
					if (this.userProfile.saveUserInCCV2) {
						const { entityId } = this.currentUserRolodexIds;
						const { userId, firstName, lastName, middleName, preferredName } =
							this.userProfile;
						const payload = {
							entityId,
							contactId: this.userProfile.rolodexContactId,
							userId,
							firstName,
							lastName,
							middleName,
							preferredName,
						};

						await leadsService.saveOwner(payload);
						this.resetSaveUserInCCV2Flag();
					}
					if (this.socialVAccountChanged) {
						await this.sendUpdatedSocialVAccount();
					}
					this.savingState = false;
					this.savedState = true;
					if (this.hasBlink && this.profile.blinkLink === '') {
						this.setBlinkIntegrationInd();
					}
					setTimeout(() => {
						this.savedState = false;
					}, 1000);
				} catch (error) {
					this.savingState = false;
				}
			} else {
				this.showInvalidItem();
			}
		},
		showInvalidItem() {
			const invalidItem = findFirstInvalidItem(
				['brand', 'profile', 'socialMediaDetails'],
				this.$v,
			);
			if (invalidItem && this.$refs[invalidItem]) {
				/** If a ref is on a components inside a v-for, the component is nested inside an array */
				const itemEl = this.$refs[invalidItem].$el
					? this.$refs[invalidItem].$el
					: this.$refs[invalidItem][0].$el;
				itemEl.parentElement.scrollIntoView(false);
				/** If the invalid item has an input inside of it, focus on it */
				let input;
				itemEl.children.forEach((item) => {
					if (item.tagName === 'INPUT') {
						input = item;
					}
				});
				if (input) {
					input.focus();
				}
			}
		},
		aboutDescriptionClickHandler() {
			this.$router.push({ path: '/preference-center/mortgagematchup' });
		},
		async sendUpdatedSocialVAccount() {
			const payload = {
				userId: this.userProfile.userId,
				firstName: this.userProfile.firstName,
				lastName: this.userProfile.lastName,
				phoneNumber: this.userProfile.mobilePhoneNumber,
				logoUrl: this.personalCompanyLogoImageUri,
				websiteUrl: this.userProfile.websiteUrl,
			};
			await this.updateSocialVAccount(payload);

			this.originalSocialVInfo = payload;
		},
	},
	created() {
		// Add constants inside created() instead of on data to prevent two-way data binding
		this.BLINK_DETAILS = BLINK_DETAILS;
		this.FONT_OPTIONS_ARRAY = FONT_OPTIONS_ARRAY;
		this.PHONE_MASKS = PHONE_MASKS;
		this.SECTION = SECTION;
		this.SOCIAL_MEDIA_URL = SOCIAL_MEDIA_URL;
		this.SOCIAL_MEDIA_DETAILS = SOCIAL_MEDIA_DETAILS;
	},
	mounted() {
		if (this.hasSocialV) {
			this.originalSocialVInfo = {
				userId: this.userProfile.userId,
				firstName: this.userProfile.firstName,
				lastName: this.userProfile.lastName,
				phoneNumber: this.userProfile.mobilePhoneNumber,
				logoUrl: this.userProfile.personalCompanyLogo
					? this.userProfile.personalCompanyLogo.imageUri
					: '',
				websiteUrl: this.userProfile.websiteUrl,
				companyLogo: this.companyProfileCompanyName,
			};
		} else {
			this.originalSocialVInfo = {};
		}
		// if for some reason there is a gap in display order ex. first address at 0 second one is at 2, calling setAddressOrder, will make sure that we order the addresses first, then iterate and make sure that the displayOrder is accurately sequential.

		this.setAddressOrder();
	},
	beforeRouteLeave(to, from, next) {
		if (!isEqual(this.profile, this.userProfile)) {
			// TODO: Use a modal for better UX
			/* eslint-disable no-alert */
			const answer = window.confirm(
				'There are unsaved changes in the form.  Are you sure you want to continue?',
			);
			next(answer);
		} else {
			next();
		}
	},
};
</script>

<style lang="scss" scoped>
.modal-content {
	height: unset;
}
.preference-center-section--brand,
.preference-center-section--about.title-description {
	grid-row-end: span 1;
}

.preference-center-section--social .title-description {
	grid-row-end: span 2;
}

.cloudinary-uploader {
	@include rem('margin-bottom', 2px);
}

.preference-center-section {
	&:last-of-type {
		margin-bottom: 0;
	}
}

.title-description {
	@for $i from 2 through 10 {
		&--span#{$i} {
			grid-row-end: span $i;
		}
	}
}

.colorpicker--userprofile.last {
	@include rem('margin-bottom', 20px);
}

/deep/ .about-input textarea {
	min-height: 120px;
}

.generate-link {
	&--wrapper {
		display: flex;
		align-items: flex-end;
	}

	&--field {
		width: 100%;

		.b360-form-input {
			background-color: $b360-color--disabled-bg;
			border-top-right-radius: 0;
			border-bottom-right-radius: 0;
		}
	}

	&--btn {
		margin-bottom: $b360-spacing-xxx-small;
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
	}
}

.save-modal /deep/ .modal-content {
	@include lg {
		height: auto;
	}
}

.error {
	color: $b360-color--red__error;
}
</style>
